(() => {
	if (window.swiperFunctions) return;

	const records = new WeakMap();
	const isElement = (value) => value instanceof Element;

	const findElement = (target, context = document) => {
		if (isElement(target)) return target;
		if (typeof target !== "string") return null;

		if (isElement(context) && context.matches(target)) {
			return context;
		}

		return context?.querySelector?.(target) || null;
	};


	const rafThrottle = (callback) => {
		let frame = null;
		let lastArguments = [];

		const throttled = (...args) => {
			lastArguments = args;

			if (frame !== null) return;

			frame = window.requestAnimationFrame(() => {
				frame = null;
				callback(...lastArguments);
			});
		};

		throttled.cancel = () => {
			if (frame === null) return;

			window.cancelAnimationFrame(frame);
			frame = null;
		};

		return throttled;
	};


	const watchResize = (element, callback) => {
		const requestUpdate = rafThrottle(callback);
		const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(requestUpdate) : null;

		observer?.observe(element);
		window.addEventListener("resize", requestUpdate, { passive: true });

		return () => {
			observer?.disconnect();
			window.removeEventListener("resize", requestUpdate);
			requestUpdate.cancel();
		};
	};

	const getInstance = (element) => {
		if (!isElement(element)) return null;

		const instance =
			records.get(element)?.instance ||
			element.swiper;

		return instance && !instance.destroyed
			? instance
			: null;
	};


	const update = (element) => {
		const instance = getInstance(element);
		if (!instance) return null;
		
		instance.update();
		return instance;
	};

	const destroy = (element, cleanStyles = true) => {
		if (!isElement(element)) return;

		const record = records.get(element);
		const instance = getInstance(element);

		record?.stopResize?.();
		if (instance) instance.destroy(true, cleanStyles);
		records.delete(element);
	};


	const init = (element, options = {}) => {
		if (!isElement(element)) {
			console.error("swiperFunctions.init requiere un elemento HTML válido.");
			return null;
		}

		if (typeof window.Swiper === "undefined") {
			console.error("SwiperJS no está cargado.");
			return null;
		}

		const {
			replaceExisting = false,
			observeResize = true,
			onResize = null,
			...swiperOptions
		} = options;

		const existingInstance = getInstance(element);

		if (existingInstance && !replaceExisting) {
			return existingInstance;
		}

		if (existingInstance) {
			destroy(element);
		}

		const instance = new window.Swiper(element, {
			watchOverflow: true,
			observer: false,
			observeParents: false,
			observeSlideChildren: false,
			resizeObserver: false,
			updateOnWindowResize: false,

			...swiperOptions,
		});

		const stopResize = observeResize ? watchResize(element, () => {
			if (instance.destroyed) return;
			instance.update();
			if (typeof onResize === "function") onResize(instance, element);
		}) : null;

		records.set(element, { instance, stopResize });
		return instance;
	};


	const bindShopifyEditor = ({
		sliderSelector,
		slideSelector = ".swiper-slide",
		initialize,
		destroyElement = destroy,
		shouldRestartAutoplay = null,
		onSectionLoad = null,
		onSectionUnload = null,
		onBlockSelect = null,
		onBlockDeselect = null,
	}) => {

		if (!sliderSelector || typeof initialize !== "function") {
			console.error("swiperFunctions.bindShopifyEditor requiere sliderSelector e initialize.");

			return () => {};
		}

		const getSectionSlider = (event) => {
			return findElement(sliderSelector, event.target);
		};

		const handleSectionLoad = (event) => {
			const sliderElement = getSectionSlider(event);

			if (sliderElement) {
				initialize(sliderElement, event);
			}

			onSectionLoad?.(event, sliderElement);
		};

		const handleSectionUnload = (event) => {
			const sliderElement = getSectionSlider(event);

			if (sliderElement) {
				destroyElement(sliderElement, event);
			}

			onSectionUnload?.(event, sliderElement);
		};

		const handleBlockSelect = (event) => {
			const slide = event.target.closest?.(slideSelector);
			const sliderElement = slide?.closest?.(sliderSelector);
			const instance = getInstance(sliderElement);

			if (!slide || !sliderElement || !instance) return;

			instance.autoplay?.stop();

			const rawSlideIndex = slide.dataset.swiperSlideIndex;
			const slideIndex = rawSlideIndex !== undefined ? Number.parseInt(rawSlideIndex, 10) : Array.from(instance.slides).indexOf(slide);

			if (Number.isNaN(slideIndex) || slideIndex < 0) return;

			if (instance.params.loop && typeof instance.slideToLoop === "function") {
				instance.slideToLoop(slideIndex, 0);
			} else {
				instance.slideTo(slideIndex, 0);
			}

			onBlockSelect?.({ event, slide, sliderElement, instance, slideIndex });
		};

		const handleBlockDeselect = (event) => {
			const slide = event.target.closest?.(slideSelector);
			const sliderElement = slide?.closest?.(sliderSelector);
			const instance = getInstance(sliderElement);

			if (!slide || !sliderElement || !instance) return;

			const canRestart = typeof shouldRestartAutoplay === "function" ? shouldRestartAutoplay({ event, slide, sliderElement, instance }) : true;
			if (canRestart && instance.autoplay && !instance.autoplay.running) {
				instance.autoplay.start();
			}

			onBlockDeselect?.({ event, slide, sliderElement, instance });
		};

		document.addEventListener("shopify:section:load", handleSectionLoad);
		document.addEventListener("shopify:section:unload", handleSectionUnload);
		document.addEventListener("shopify:block:select", handleBlockSelect);
		document.addEventListener("shopify:block:deselect", handleBlockDeselect);

		return () => {
			document.removeEventListener("shopify:section:load", handleSectionLoad);
			document.removeEventListener("shopify:section:unload", handleSectionUnload);
			document.removeEventListener("shopify:block:select", handleBlockSelect);
			document.removeEventListener("shopify:block:deselect", handleBlockDeselect);
		};
	};

	window.swiperFunctions = { init, getInstance, update, destroy, bindShopifyEditor};
	document.dispatchEvent(new CustomEvent("swiper-functions:ready"));
})();